import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../config/authenticatedFetch';
import './CheckinCaixa.css';

interface RegistrationData {
  id?: string;
  external_id: string;
  name: string;
  field_1: string;  // Campo genérico 1
  field_2: string;  // Campo genérico 2
  field_3: string;  // Campo genérico 3
  photo_url?: string;
}

const CheckinCaixa: React.FC = () => {
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scannedCode, setScannedCode] = useState<string>('');
  const [qrDetected, setQrDetected] = useState<boolean>(false);
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [hasValidCode, setHasValidCode] = useState<boolean>(false);
  const [processingCheckin, setProcessingCheckin] = useState<boolean>(false);
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  
  const [shouldStartScanning, setShouldStartScanning] = useState<boolean>(true); // Iniciar automaticamente

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // useEffect para permitir scroll nesta página
  useEffect(() => {
    // Adicionar classe para permitir scroll no html e body
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    
    return () => {
      // Remover classe ao sair da página
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      
      // Cleanup da câmera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, []);

  // useEffect para buscar token CSRF ao montar o componente
  useEffect(() => {
    fetchCSRFToken();
  }, []);

  // useEffect para inicializar câmera quando o DOM está pronto
  useEffect(() => {
    if (shouldStartScanning && videoRef.current && canvasRef.current) {
      initializeCamera();
    }
  }, [shouldStartScanning, isScanning]);

  // Função para buscar token CSRF
  const fetchCSRFToken = async (): Promise<string> => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.API_CSRF_TOKEN, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setCsrfToken(data.csrfToken);
        return data.csrfToken;
      } else {
        return '';
      }
    } catch (error) {
      return '';
    }
  };

  // Função para confirmar checkin
  const confirmCheckin = async () => {
    if (!registrationData) return;
    
    setProcessingCheckin(true);
    setError('');
    setSuccess('');
    
    try {
      
      // Garantir que temos o token CSRF
      let currentToken = csrfToken;
      if (!currentToken) {
        currentToken = await fetchCSRFToken();
        if (!currentToken) {
          setError('Erro ao obter token de segurança. Tente novamente.');
          return;
        }
      }
      
      const checkinData = {
        idParticipante: registrationData.external_id,
        tipo_checkin: "caixa"
      };
      
      // Headers com token CSRF
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (currentToken) {
        headers['X-CSRFToken'] = currentToken;
      }
      
      const response = await authenticatedFetch(API_ENDPOINTS.API_CHECKIN, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(checkinData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mostrar tela de sucesso
        setShowSuccessScreen(true);
        setSuccess('✅ Check-in realizado com sucesso! Bem-vindo à Caixa!');
        
        // Esconder tela de sucesso e reiniciar scanner após 3 segundos
        setTimeout(() => {
          setShowSuccessScreen(false);
          clearData();
          startScanning(); // Reiniciar o scanner automaticamente
        }, 3000);
      } else {
        setError(data.message || 'Erro ao realizar check-in. Tente novamente.');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor. Verifique a conexão.');
    } finally {
      setProcessingCheckin(false);
    }
  };

  const initializeCamera = async () => {
    try {
      
      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Câmera não suportada neste navegador');
        setIsScanning(false);
        setShouldStartScanning(false);
        return;
      }

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Preferir câmera traseira
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      streamRef.current = stream;
      
      // Aguardar o video estar pronto
      const video = videoRef.current!;
      video.srcObject = stream;
      
      // Aguardar o vídeo carregar
      video.onloadedmetadata = () => {
        video.play().then(() => {
          
          // Iniciar detecção de QR code com frequência otimizada (3x por segundo)
          scanIntervalRef.current = setInterval(() => {
            captureFrame();
          }, 330); // ~3 FPS para melhor performance
          
          setShouldStartScanning(false); // Reset flag
          
        }).catch((playError) => {
          setError('Erro ao iniciar visualização da câmera');
          stopScanning();
        });
      };

    } catch (err: any) {
      
      let errorMessage = 'Erro ao acessar a câmera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Permita o acesso e tente novamente.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Câmera não atende aos requisitos especificados.';
      }
      
      setError(errorMessage);
      setIsScanning(false);
      setShouldStartScanning(false);
    }
  };

  const startScanning = async () => {
    try {
      setError('');
      setSuccess('');
      setQrDetected(false); // Reset QR detection state
      setShouldStartScanning(true);
      setIsScanning(true);
      
      // Aguardar um breve momento para o DOM se atualizar
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!videoRef.current || !canvasRef.current) {
        setError('Erro interno: elementos não encontrados');
        setIsScanning(false);
        return;
      }

      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Câmera não suportada neste navegador');
        setIsScanning(false);
        return;
      }

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Preferir câmera traseira
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      streamRef.current = stream;
      
      // Aguardar o video estar pronto
      const video = videoRef.current;
      video.srcObject = stream;
      
      // Aguardar o vídeo carregar
      video.onloadedmetadata = () => {
        video.play().then(() => {
          
          // Iniciar detecção de QR code com frequência otimizada (3x por segundo)
          scanIntervalRef.current = setInterval(() => {
            captureFrame();
          }, 330); // ~3 FPS para melhor performance
          
        }).catch((playError) => {
          setError('Erro ao iniciar visualização da câmera');
          stopScanning();
        });
      };

    } catch (err: any) {
      
      let errorMessage = 'Erro ao acessar a câmera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Permita o acesso e tente novamente.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Câmera não atende aos requisitos especificados.';
      }
      
      setError(errorMessage);
      setIsScanning(false);
    }
  };

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) {
      return;
    }
    
    // Verificar se o vídeo tem dimensões válidas
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Obter dados da imagem para processamento de QR code
    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      // Tentar detectar QR code usando jsQR
      const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      
      if (qrCode) {
        
        // Indicar que um QR foi detectado
        setQrDetected(true);
        
        // Parar o scanning quando detectar um QR code
        stopScanning();
        
        // Processar o QR code detectado
        handleQRCodeDetected(qrCode.data);
        return; // Sair da função após detectar
      }
      
    } catch (error) {
      // Silenciar erros de processamento
    }
  };

  const stopScanning = () => {
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (videoRef.current) {
      console.log('📺 Limpando srcObject do vídeo');
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
    console.log('✅ Scanner parado com sucesso');
  };

  const handleManualCodeInput = (code: string) => {
    if (code.trim()) {
      setScannedCode(code.trim());
      handleQRCodeDetected(code.trim());
    }
  };

  const testCameraPermissions = async () => {
    try {
      console.log('🔍 Testando permissões da câmera...');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('API de mídia não suportada neste navegador');
        return;
      }

      // Listar dispositivos disponíveis
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      if (videoDevices.length === 0) {
        setError('Nenhuma câmera encontrada no dispositivo');
        return;
      }

      // Testar acesso básico
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Parar imediatamente o teste
      stream.getTracks().forEach(track => track.stop());
      
      setSuccess('✅ Câmera testada com sucesso! Você pode usar o scanner.');
      
    } catch (err: any) {
      let errorMessage = 'Erro ao testar câmera: ';
      
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Permissão negada. Permita o acesso à câmera nas configurações do navegador.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'Nenhuma câmera encontrada.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleQRCodeDetected = async (code: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setScannedCode(code); // Salvar o código escaneado

    try {
      
      // Tentar extrair ID do participante de diferentes formatos de QR code
      let participantId = code;
      
      // Se for JSON, tentar extrair o ID
      try {
        const jsonData = JSON.parse(code);
        if (jsonData.idParticipante) {
          participantId = jsonData.idParticipante;
        } else if (jsonData.id) {
          participantId = jsonData.id;
        } else if (jsonData.external_id) {
          participantId = jsonData.external_id;
        }
        console.log('📋 ID extraído do JSON:', participantId);
      } catch (e) {
        // Não é JSON, usar como string simples
        console.log('📝 Usando código como string simples:', participantId);
      }

      console.log('🔎 Buscando registro para ID:', participantId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_GETBYID}?idParticipante=${participantId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      console.log('📊 Resposta da API:', data);

      if (data.success && data.data) {
        // Registro encontrado
        setRegistrationData(data.data);
        setSuccess('✅ Registro encontrado! Dados carregados para check-in.');
        setHasValidCode(true); // Marca que um código válido foi processado
        
        // Parar o scanner quando dados válidos forem encontrados
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (scanIntervalRef.current) {
          clearInterval(scanIntervalRef.current);
        }
        setIsScanning(false);
        setShouldStartScanning(false);
        
      } else {
        // Registro não encontrado
        setRegistrationData(null);
        showErrorMessage('Registro não encontrado');
        return; // Sair da função para não marcar como código válido
      }
    } catch (err) {
      console.error('❌ Erro ao buscar registro:', err);
      showErrorMessage('Código inválido');
      return; // Sair da função em caso de erro
    } finally {
      setLoading(false);
    }
  };

  // Função para mostrar tela de erro
  const showErrorMessage = (message: string) => {
    setError(message);
    setShowErrorScreen(true);
    setIsScanning(false);
    
    // Parar o scanner atual
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    
    // Esconder a tela de erro após 2 segundos e retomar o scanner
    setTimeout(() => {
      setShowErrorScreen(false);
      setError('');
      // Reinicializar o scanner chamando startScanning
      startScanning();
    }, 2000);
  };

  const clearData = () => {
    setScannedCode('');
    setRegistrationData(null);
    setError('');
    setSuccess('');
    setHasValidCode(false); // Reset da validação do código
    setShowErrorScreen(false); // Reset da tela de erro
    setShowSuccessScreen(false); // Reset da tela de sucesso
    
    // Reiniciar o scanner automaticamente
    startScanning();
  };

  return (
    <div className="checkin-caixa-container">
      {/* Tela de Erro */}
      {showErrorScreen && (
        <div className="error-screen">
          <div className="error-content">
            <div className="error-icon">❌</div>
            <h2>Código Inválido</h2>
            <p>Registro não encontrado</p>
          </div>
        </div>
      )}

      {/* Tela de Sucesso */}
      {showSuccessScreen && (
        <div className="success-screen">
          <div className="success-content">
            <div className="success-icon">✅</div>
            <h2>Check-in Realizado!</h2>
          </div>
        </div>
      )}

      {/* Tela de Check-in com Dados (Sobreposição) */}
      {registrationData && hasValidCode && (
        <div className="checkin-overlay">
          <div className="checkin-overlay-content">
            <div className="data-header">
              <h2>📦 Check-in da Caixa</h2>
              <div className="record-info">
                <small>ID: {registrationData.id}</small>
              </div>
            </div>

            <div className="participant-info">
              <div className="info-row">
                <div className="info-group">
                  <label>ID Participante:</label>
                  <div className="info-value">{registrationData.external_id}</div>
                </div>
                <div className="info-group">
                  <label>Nome:</label>
                  <div className="info-value">{registrationData.name || 'Não informado'}</div>
                </div>
              </div>

              <div className="info-row">
                <div className="info-group">
                  <label>Campo 2:</label>
                  <div className="info-value">{registrationData.field_2 || 'Não informado'}</div>
                </div>
              </div>


              {registrationData.photo_url && (
                <div className="info-group photo-section">
                  <label>Foto:</label>
                  <div className="photo-display">
                    <img 
                      src={registrationData.photo_url} 
                      alt="Foto do participante" 
                      className="participant-photo"
                    />
                  </div>
                </div>
              )}

              <div className="actions">
                <button
                  onClick={clearData}
                  className="btn btn-outline"
                  style={{ marginLeft: '10px' }}

                >
                  ❌ Cancelar
                </button>
                <button
                  onClick={confirmCheckin}
                  disabled={processingCheckin}
                  className="btn btn-primary"
                  style={{ marginLeft: '10px' }}
                >
                  {processingCheckin ? '⏳ Processando...' : '✅ Confirmar Check-in'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interface principal - oculta quando há dados válidos */}
      {!(registrationData && hasValidCode) && (
        <>
          <div className="checkin-caixa-header">
            <h1>📦 Check-in Caixa</h1>
            <p>Escaneie um QR code para fazer check-in</p>
          </div>

          {/* Scanner Section */}
          <div className="scanner-section">
            <div className="scanner-controls">
              <div className="button-group">
                <button
                  onClick={testCameraPermissions}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  🔧 Testar Câmera
                </button>
                
                {!isScanning ? (
                  <button 
                    onClick={startScanning}
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    📷 Iniciar Scanner
                  </button>
                ) : (
                  <button 
                    onClick={stopScanning}
                    className="btn btn-secondary"
                  >
                    ⏹️ Parar Scanner
                  </button>
                )}
              </div>
            </div>

            {/* Input manual para teste */}
            <div className="manual-input-section">
              <h3>Ou digite o código manualmente:</h3>
              <div className="manual-input-group">
                <input
                  type="text"
                  placeholder="Digite o código (ex: USER_002)"
                  className="form-control"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleManualCodeInput((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('.manual-input-group input') as HTMLInputElement;
                    if (input) {
                      handleManualCodeInput(input.value);
                      input.value = '';
                    }
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  🔍 Buscar
                </button>
              </div>
            </div>

            {isScanning && (
              <div className="scanner-container">
                <video 
                  ref={videoRef}
                  className="scanner-video"
                  autoPlay
                  playsInline
                  muted
                />
                <canvas 
                  ref={canvasRef}
                  style={{ display: 'none' }}
                />
                <div className="scanner-overlay">
                  
                </div>
              </div>
            )}

            {scannedCode && (
              <div className="scanned-result">
                <strong>Código detectado:</strong> {scannedCode}
              </div>
            )}
          </div>

          {/* Messages */}
          {loading && <div className="message loading">⏳ Processando...</div>}
          {error && <div className="message error">❌ {error}</div>}
          {success && <div className="message success">✅ {success}</div>}
          
          {/* Warning message when no valid code */}
          {!hasValidCode && !loading && (
            <div className="message warning">
              ⚠️ É necessário escanear um QR code ou inserir um código manualmente para realizar o check-in.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CheckinCaixa;
